import { defineStore } from 'pinia'
import { getAccounts, getAccountInfos, changeMemberStatus } from '@/api/account'
import { jwtDecode } from 'jwt-decode'
import { setCurrentAccount, getLastAccountId, CURRENT_ACCOUNT_SIGNATURE_ATTR } from '@/utils/account-manager'
import { usePermissionStore } from './permission'
import { useAuthStore } from './auth'
import type { Account } from '@/models/Account'

interface AccountData {
  roles: string[]
  cff: string[]
  account_id?: string
  account_name?: string
  domain?: string
  exp?: number
  [key: string]: unknown
}

interface AccountState {
  accounts: Account[]
}

// Singleton in-flight promise — prevents concurrent account fetch calls
let currentAccountPromise: Promise<{ signature: string; data: AccountData }> | null = null

// The stored signature carries roles and feature flags (`cff`) as they were when it was issued, up
// to an hour ago. Like the access token, it is fetched anew once per page load, so a reload shows
// a changed role or flag right away.
let signatureFetchedThisLoad = false

export const useAccountStore = defineStore('account', {
  state: (): AccountState => ({
    accounts: []
  }),

  actions: {
    resetState() {
      this.accounts = []
      currentAccountPromise = null
      signatureFetchedThisLoad = false
    },

    async getAllAccounts(force: boolean = false): Promise<Account[]> {
      if (force) {
        this.accounts = []
      }

      if (this.accounts.length) {
        return this.accounts
      }

      const resp = await getAccounts()
      this.accounts = resp.data.data
      return this.accounts
    },

    async getCurrentAccount(force: boolean = false): Promise<{ signature: string; data: AccountData }> {
      const permissionStore = usePermissionStore()
      const authStore = useAuthStore()

      if (force) {
        localStorage.removeItem(CURRENT_ACCOUNT_SIGNATURE_ATTR)
        currentAccountPromise = null
      }

      // Return cached signature synchronously if available
      let signature = localStorage.getItem(CURRENT_ACCOUNT_SIGNATURE_ATTR)
      let accountData: AccountData | null = null

      try {
        if (signature) {
          accountData = jwtDecode<AccountData>(signature)
        }
      } catch {
        // Signature invalid, clear it
        signature = null
      }

      if (signature && accountData) {
        const exp = accountData.exp
        const isExpired = exp ? exp * 1000 < Date.now() : false
        if (!isExpired && (signatureFetchedThisLoad || !authStore.isAuthenticated)) {
          permissionStore.setRoles(accountData.roles)
          return { signature, data: accountData }
        }
        if (!isExpired) {
          // First use on this page load: fetch a fresh one, keep the stored one if that fails.
          const stored = { signature, data: accountData }
          try {
            return await this.fetchCurrentAccount(accountData.account_id)
          } catch {
            signatureFetchedThisLoad = true
            permissionStore.setRoles(stored.data.roles)
            return stored
          }
        }
        // Signature expired — clear it and fetch a fresh one of the same account
        const expiredAccountId = accountData.account_id
        localStorage.removeItem(CURRENT_ACCOUNT_SIGNATURE_ATTR)
        if (!authStore.isAuthenticated) {
          throw new Error('Not authenticated')
        }
        return this.fetchCurrentAccount(expiredAccountId)
      }

      // Need to fetch account — use singleton promise to prevent concurrent fetches
      if (!authStore.isAuthenticated) {
        throw new Error('Not authenticated')
      }

      return this.fetchCurrentAccount()
    },

    /**
     * A fresh signature for the acting account: `accountId` (the one in use), else the account
     * last chosen in this browser, else the only one. A person with several accounts and no
     * choice yet gets an error — the caller sends them to the /accounts picker.
     */
    fetchCurrentAccount(accountId?: string): Promise<{ signature: string; data: AccountData }> {
      if (!currentAccountPromise) {
        currentAccountPromise = (async () => {
          const resp = await getAccounts()
          const accounts = resp.data.data
          this.accounts = accounts
          const wanted = accountId ?? getLastAccountId()
          const account =
            accounts.find((a) => a.id === wanted) ?? (accounts.length === 1 ? accounts[0] : undefined)
          if (!account?.signature) {
            throw new Error('No account chosen')
          }
          setCurrentAccount(account.signature)
          signatureFetchedThisLoad = true
          const data = jwtDecode<AccountData>(account.signature)
          return { signature: account.signature, data }
        })().finally(() => {
          currentAccountPromise = null
        })
      }

      return currentAccountPromise
    },

    async getAccountDetails() {
      const resp = await getAccountInfos()
      return resp.data
    },

    async changeMemberStatus(userId: string, status: string) {
      const resp = await changeMemberStatus(userId, status)
      return resp
    }
  }
})
